
import React, { useEffect, useState } from 'react';
import { VideoEvent, VideoFilterOptions } from '@/hooks/useVideoEvents';
import { fetchProfileName } from '@/components/dashboard/profiles/content/videos/services/profileService';
import { groupEventsByDate } from './list/eventGrouping';
import DayGroup from './list/DayGroup';
import EmptyState from './list/EmptyState';
import MonthHeader from './list/MonthHeader';
import VideoDetailsDialog from '../components/VideoDetailsDialog';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import { SubscriptionLock } from '@/components/subscription/SubscriptionLock';

interface ListViewProps {
  videoEvents: VideoEvent[];
  onVideoClick: (videoId?: string) => void;
  filterOptions?: VideoFilterOptions;
}

const ListView: React.FC<ListViewProps> = ({ videoEvents, onVideoClick, filterOptions }) => {
  const [channelNames, setChannelNames] = useState<Record<string, string>>({});
  const [selectedVideo, setSelectedVideo] = useState<VideoEvent | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { isSubscriptionActive } = useSubscriptionStatus();

  // Fetch channel names for all profile IDs
  useEffect(() => {
    const fetchChannelNames = async () => {
      const profileIds = [...new Set(videoEvents.map(event => event.profileId))];
      const results: Record<string, string> = {};
      
      for (const profileId of profileIds) {
        try {
          const name = await fetchProfileName(profileId);
          results[profileId] = name;
        } catch (error) {
          console.error(`Error fetching name for profile ${profileId}:`, error);
          results[profileId] = "Chaîne inconnue";
        }
      }
      
      setChannelNames(results);
    };
    
    if (videoEvents.length > 0) {
      fetchChannelNames();
    }
  }, [videoEvents]);

  const handleVideoClick = (event: VideoEvent) => {
    setSelectedVideo(event);
    setIsDialogOpen(true);
    // Still call onVideoClick for any tracking purposes, but without navigating
    if (onVideoClick) onVideoClick(event.videoId);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedVideo(null);
  };

  const { groupedEvents, sortedDates } = groupEventsByDate(videoEvents);

  // If subscription is not active, show the lock screen
  if (!isSubscriptionActive) {
    return (
      <SubscriptionLock 
        title="Liste des événements verrouillée"
        description="La liste détaillée des événements n'est disponible qu'avec un abonnement actif."
        buttonText="Débloquer le calendrier"
      />
    );
  }

  return (
    <div className="p-6 overflow-auto max-w-4xl mx-auto">
      {sortedDates.length > 0 ? (
        <>
          {/* Month display at the top */}
          {sortedDates.length > 0 && (
            <MonthHeader dateStr={sortedDates[0]} sortedDates={sortedDates} />
          )}
          
          {/* Events grouped by date */}
          {sortedDates.map(dateStr => (
            <DayGroup 
              key={dateStr}
              dateStr={dateStr}
              events={groupedEvents[dateStr]}
              channelNames={channelNames}
              onVideoClick={(videoId) => {
                // Find the event with the matching videoId
                const event = videoEvents.find(event => event.videoId === videoId);
                if (event) {
                  handleVideoClick(event);
                }
              }}
            />
          ))}
        </>
      ) : (
        <EmptyState filterOptions={filterOptions} />
      )}
      
      <VideoDetailsDialog 
        video={selectedVideo}
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
      />
    </div>
  );
};

export default ListView;
